import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

const LOGO = "/images/garcia-burns-logo.png";
const PORTRAIT = "/images/dr-garcia-burns.jpg";
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

const NAV = [
  { label: "Ethos", href: "/#ethos" },
  { label: "Companies", href: "/#companies" },
  { label: "Founder", to: "/founder" },
  { label: "Contact", href: "/#contact" },
];

const COMPANIES = [
  {
    no: "01",
    name: "LucentisIQ",
    tag: "Transformation Intelligence",
    desc:
      "Helps organizations reduce ambiguity, improve readiness, and drive adoption in complex change environments.",
    status: "Active",
    href: "https://lucentisiq.com/",
    external: true,
  },
  {
    no: "02",
    name: "Garcia Burns Advisory",
    tag: "Executive Advisory",
    desc:
      "Transformation strategy, leadership effectiveness, organizational design, and workforce modernization for the boardroom.",
    status: "Active",
    href: "/#contact",
  },
  {
    no: "03",
    name: "Garcia Burns Institute",
    tag: "Research & Education",
    desc:
      "A future research and education platform advancing executive learning, transformation capability, and thought leadership.",
    status: "In Development",
    href: "/#contact",
  },
  {
    no: "04",
    name: "Garcia Burns Ventures",
    tag: "Strategic Investment",
    desc:
      "Future investment arm focused on founders, technologies, and businesses shaping the future of work and human potential.",
    status: "In Development",
    href: "/#contact",
  },
];

const PILLARS = [
  { k: "Transformation", v: "Clarity over noise" },
  { k: "Intelligence", v: "Decisions at speed" },
  { k: "Leadership", v: "Built to endure" },
  { k: "Potential", v: "Realized at scale" },
];

const MARQUEE_WORDS = [
  "Transformation",
  "Artificial Intelligence",
  "Leadership",
  "Human Potential",
  "Strategy",
  "Readiness",
  "Adoption",
  "Future of Work",
];

/* ---------- small atoms ---------- */

/* Smart nav link — handles cross-page hash navigation gracefully */
const SmartNavLink = ({ href, to, onClick, children, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();

  if (to) {
    return (
      <Link
        to={to}
        onClick={(e) => {
          onClick?.(e);
        }}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const handle = (e) => {
    onClick?.(e);
    if (!href) return;
    if (href.startsWith("/#")) {
      e.preventDefault();
      const hash = href.slice(1); // "#ethos"
      if (location.pathname !== "/") {
        navigate("/" + hash);
      } else {
        document
          .querySelector(hash)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <a href={href} onClick={handle} {...rest}>
      {children}
    </a>
  );
};

/* On route change, scroll to hash target if present, otherwise to top */
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    return undefined;
  }, [pathname, hash]);
  return null;
};

const Eyebrow = ({ children, testid }) => (
  <span className="eyebrow inline-flex items-center gap-3" data-testid={testid}>
    <span className="inline-block w-6 h-px bg-[var(--gbg-gold)] opacity-70" />
    {children}
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => e.target.classList.add("in"), delay);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

/* ---------- Header ---------- */

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(10,9,7,0.78)] backdrop-blur-xl border-b border-[rgba(201,163,82,0.18)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO}
            alt="Garcia Burns Group"
            className="w-9 h-9 object-contain transition-transform duration-700 group-hover:rotate-[8deg]"
          />
          <div className="leading-tight">
            <div className="font-italiana text-[15px] tracking-[0.18em] text-[var(--gbg-cream)]">
              GARCIA&nbsp;BURNS
            </div>
            <div className="text-[9px] tracking-[0.4em] text-[var(--gbg-gold)] mt-0.5">
              GROUP&nbsp;·&nbsp;LLC
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10" data-testid="primary-nav">
          {NAV.map((n) => (
            <SmartNavLink
              key={n.label}
              href={n.href}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="link-underline text-[11px] tracking-[0.28em] uppercase text-[var(--gbg-cream)]/85 hover:text-[var(--gbg-gold)] transition-colors"
            >
              {n.label}
            </SmartNavLink>
          ))}
          <a
            href="mailto:info@garciaburnsgroup.com"
            data-testid="nav-cta"
            className="text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[var(--gbg-gold)]/60 text-[var(--gbg-gold)] hover:bg-[var(--gbg-gold)] hover:text-[var(--gbg-ink)] transition-all duration-500"
          >
            Inquire
          </a>
        </nav>

        <button
          className="md:hidden text-[var(--gbg-cream)] p-2"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <span className="block w-6 h-px bg-current mb-1.5" />
          <span className="block w-6 h-px bg-current mb-1.5" />
          <span className="block w-4 h-px bg-current ml-auto" />
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[rgba(10,9,7,0.96)] border-t border-[rgba(201,163,82,0.18)]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <SmartNavLink
                key={n.label}
                href={n.href}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.label.toLowerCase()}`}
                className="text-[12px] tracking-[0.28em] uppercase text-[var(--gbg-cream)]/85"
              >
                {n.label}
              </SmartNavLink>
            ))}
            <a
              href="mailto:info@garciaburnsgroup.com"
              className="text-[12px] tracking-[0.28em] uppercase text-[var(--gbg-gold)]"
              data-testid="mnav-inquire"
            >
              Inquire →
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------- Hero ---------- */

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], ["-50%", "-30%"]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const logoOpacity = useTransform(scrollYProgress, [0, 1], [0.18, 0.05]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* ambient gold radial */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 45%, rgba(201,163,82,0.18) 0%, rgba(10,9,7,0) 70%)",
        }}
      />
      {/* logo aura */}
      <motion.img
        src={LOGO}
        alt=""
        aria-hidden
        className="absolute left-1/2 top-1/2 w-[820px] max-w-[120vw] drift"
        style={{
          y: logoY,
          x: "-50%",
          scale: logoScale,
          opacity: logoOpacity,
          filter: "blur(0.5px)",
        }}
      />
      {/* vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-12 w-full pt-32 pb-24">
        <Reveal>
          <Eyebrow testid="hero-eyebrow">
            Strategic Holdings · Advisory · Innovation
          </Eyebrow>
        </Reveal>

        <Reveal delay={120}>
          <h1
            data-testid="hero-headline"
            className="font-display mt-8 text-[clamp(52px,8.4vw,148px)] leading-[0.92] tracking-[-0.02em] max-w-[14ch]"
          >
            Building <em className="not-italic gold-sheen">what&apos;s next</em>
            <br />
            <span className="text-[var(--gbg-cream)]/90">in transformation.</span>
          </h1>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-12 gap-10 items-end">
          <Reveal delay={240} className="md:col-span-7">
            <p
              data-testid="hero-sub"
              className="text-[17px] md:text-[19px] leading-[1.75] text-[var(--gbg-bone)] max-w-[58ch] font-light"
            >
              Garcia Burns Group LLC is a strategic holding company that builds,
              owns, and scales businesses focused on transformation, artificial
              intelligence, leadership, and human potential.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#companies"
                data-testid="hero-cta-companies"
                className="group inline-flex items-center gap-3 px-6 py-4 border border-[var(--gbg-gold)] text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-gold)] hover:bg-[var(--gbg-gold)] hover:text-[var(--gbg-ink)] transition-all duration-500"
              >
                <span>Explore the Portfolio</span>
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="link-underline text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-cream)]/85"
              >
                Begin a conversation
              </a>
            </div>
          </Reveal>

          <Reveal delay={360} className="md:col-span-5">
            <div className="border-l border-[rgba(201,163,82,0.35)] pl-6">
              <div className="font-italiana text-[12px] tracking-[0.4em] text-[var(--gbg-gold)] mb-3">
                EST.&nbsp;2024
              </div>
              <div className="font-display text-[22px] leading-[1.45] text-[var(--gbg-cream)]/90">
                A house of ventures designed for the age of intelligent
                organizations.
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* bottom scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="eyebrow text-[var(--gbg-muted)]">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-[var(--gbg-gold)] to-transparent" />
      </div>
    </section>
  );
};

/* ---------- Marquee strip ---------- */

const Marquee = () => (
  <section
    aria-hidden
    data-testid="marquee"
    className="relative border-y border-[rgba(201,163,82,0.18)] bg-[var(--gbg-ink-2)] overflow-hidden py-7"
  >
    <div className="marquee gap-16 whitespace-nowrap font-italiana text-[clamp(28px,3.4vw,44px)] tracking-[0.18em] text-[var(--gbg-cream)]/35">
      {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
        <span key={`${w}-${i}`} className="flex items-center gap-16">
          {w}
          <span className="text-[var(--gbg-gold)]">✦</span>
        </span>
      ))}
    </div>
  </section>
);

/* ---------- Ethos ---------- */

const Ethos = () => (
  <section
    id="ethos"
    data-testid="ethos"
    className="relative py-28 md:py-40 px-6 md:px-12"
  >
    <div className="max-w-[1280px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
      <Reveal className="md:col-span-4">
        <Eyebrow>The Ethos · 01</Eyebrow>
        <div className="font-italiana text-[var(--gbg-gold)] text-[13px] tracking-[0.4em] mt-10">
          A QUIET HOUSE
          <br />
          OF LOUD IDEAS
        </div>
      </Reveal>

      <Reveal delay={120} className="md:col-span-8">
        <h2 className="font-display text-[clamp(34px,4.2vw,64px)] leading-[1.05] tracking-[-0.01em] text-[var(--gbg-cream)]">
          Where <em className="not-italic gold-sheen">strategy</em>, technology,
          and people converge.
        </h2>
        <p className="mt-8 max-w-[62ch] text-[17px] md:text-[19px] leading-[1.8] text-[var(--gbg-bone)] font-light">
          In the age of AI, organizations do not need more noise. They need
          clarity, alignment, trust, and intelligent execution. Garcia Burns
          Group creates ventures that help organizations navigate complexity and
          deliver outcomes that last.
        </p>

        <div className="mt-14 grid sm:grid-cols-2 gap-x-10 gap-y-6">
          {PILLARS.map((p, i) => (
            <div
              key={p.k}
              data-testid={`pillar-${i}`}
              className="flex items-baseline justify-between py-4 border-t border-[rgba(201,163,82,0.22)]"
            >
              <span className="font-display text-[22px] text-[var(--gbg-cream)]">
                {p.k}
              </span>
              <span className="eyebrow text-[var(--gbg-muted)] translate-x-4">{p.v}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Companies ---------- */

const CompanyCardInner = ({ c }) => (
  <>
    {/* hover sheen */}
    <span
      aria-hidden
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
      style={{
        background:
          "radial-gradient(120% 80% at 100% 0%, rgba(201,163,82,0.14) 0%, transparent 60%)",
      }}
    />
    <div className="relative flex items-start justify-between mb-10">
      <span className="font-italiana text-[var(--gbg-gold)] tracking-[0.3em] text-[12px]">
        /&nbsp;{c.no}
      </span>
      <span className="eyebrow text-[10px] text-[var(--gbg-muted)] border border-[rgba(201,163,82,0.25)] px-3 py-1">
        {c.status}
      </span>
    </div>
    <h3 className="relative font-display text-[30px] md:text-[34px] leading-[1.1] text-[var(--gbg-cream)] group-hover:text-[var(--gbg-gold-bright)] transition-colors duration-500">
      {c.name}
    </h3>
    <div className="relative eyebrow mt-3 text-[var(--gbg-gold)]/80">{c.tag}</div>
    <p className="relative mt-7 text-[15px] leading-[1.75] text-[var(--gbg-bone)] font-light max-w-[40ch]">
      {c.desc}
    </p>
    <div className="relative mt-10 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-gold)]">
      <span>{c.external ? "Visit Site" : "Learn More"}</span>
      <span className="inline-block w-8 h-px bg-[var(--gbg-gold)] transition-all duration-500 group-hover:w-16" />
      {c.external && (
        <span className="text-[var(--gbg-gold)] transition-transform duration-500 group-hover:translate-x-1">
          ↗
        </span>
      )}
    </div>
  </>
);

const CARD_CLASS =
  "group relative block h-full p-8 md:p-10 border border-[rgba(201,163,82,0.22)] bg-gradient-to-b from-[rgba(255,255,255,0.025)] to-[rgba(255,255,255,0.005)] overflow-hidden transition-all duration-700 hover:border-[var(--gbg-gold)]";

const CompanyCard = ({ c, i }) => (
  <Reveal delay={i * 80}>
    {c.external ? (
      <a
        href={c.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`company-${c.no}`}
        className={CARD_CLASS}
      >
        <CompanyCardInner c={c} />
      </a>
    ) : (
      <SmartNavLink
        href={c.href}
        data-testid={`company-${c.no}`}
        className={CARD_CLASS}
      >
        <CompanyCardInner c={c} />
      </SmartNavLink>
    )}
  </Reveal>
);

const Companies = () => (
  <section
    id="companies"
    data-testid="companies"
    className="relative py-28 md:py-40 px-6 md:px-12 bg-[var(--gbg-ink-2)] border-y border-[rgba(201,163,82,0.18)]"
  >
    <div className="max-w-[1280px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12 items-end mb-16 md:mb-20">
        <Reveal className="md:col-span-7">
          <Eyebrow>The Portfolio · 02</Eyebrow>
          <h2 className="font-display mt-6 text-[clamp(38px,5vw,76px)] leading-[1.02] tracking-[-0.01em]">
            Multiple ventures.
            <br />
            <em className="not-italic gold-sheen">One vision.</em>
          </h2>
        </Reveal>
        <Reveal delay={120} className="md:col-span-5">
          <p className="text-[16px] leading-[1.8] text-[var(--gbg-bone)] font-light">
            A vertically integrated house of advisory, intelligence, research,
            and capital — designed to compound expertise across every stage of
            enterprise transformation.
          </p>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        {COMPANIES.map((c, i) => (
          <CompanyCard key={c.no} c={c} i={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Founder ---------- */

const EXPERTISE = [
  "Transformation Strategy",
  "Organizational Change",
  "Leadership Effectiveness",
  "AI & Workforce Transformation",
  "Manufacturing Transformation",
  "Program & Portfolio Leadership",
  "Workforce Readiness & Adoption",
  "Executive Advisory",
  "Learning & Capability Development",
];

/* ---------- Founder (teaser on home) + FounderPage (/founder) ---------- */

const FounderPortraitCard = ({ className = "" }) => (
  <div
    className={`aspect-[4/5] relative overflow-hidden border border-[rgba(201,163,82,0.3)] bg-[#0a0907] ${className}`}
  >
    <img
      src={PORTRAIT}
      alt="Dr. Ashley C. Garcia Burns, Founder & CEO of Garcia Burns Group LLC"
      data-testid="founder-portrait"
      className="absolute inset-0 w-full h-full object-cover"
      loading="lazy"
    />
    <div
      className="absolute inset-0 pointer-events-none mix-blend-soft-light"
      style={{
        background:
          "linear-gradient(180deg, rgba(201,163,82,0.06) 0%, rgba(201,163,82,0) 35%, rgba(201,163,82,0) 65%, rgba(10,9,7,0.45) 100%)",
      }}
    />
    <div
      className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,9,7,0) 0%, rgba(10,9,7,0.85) 90%)",
      }}
    />
    <div className="absolute left-6 right-6 bottom-6 z-10">
      <div className="font-italiana text-[11px] tracking-[0.4em] text-[var(--gbg-gold)]">
        FOUNDER · CEO
      </div>
      <div className="font-display text-[22px] md:text-[24px] leading-[1.15] text-[var(--gbg-cream)] mt-1">
        Dr. Ashley C. Garcia Burns
      </div>
    </div>
    <span className="absolute top-3 left-3 w-5 h-5 border-l border-t border-[var(--gbg-gold)] z-10" />
    <span className="absolute top-3 right-3 w-5 h-5 border-r border-t border-[var(--gbg-gold)] z-10" />
    <span className="absolute bottom-3 left-3 w-5 h-5 border-l border-b border-[var(--gbg-gold)] z-10" />
    <span className="absolute bottom-3 right-3 w-5 h-5 border-r border-b border-[var(--gbg-gold)] z-10" />
  </div>
);

/* Home page teaser */
const FounderTeaser = () => (
  <section
    id="founder"
    data-testid="founder-teaser"
    className="relative py-28 md:py-36 px-6 md:px-12 overflow-hidden"
  >
    <img
      src={LOGO}
      alt=""
      aria-hidden
      className="absolute -left-40 top-20 w-[520px] opacity-[0.04] pointer-events-none"
    />
    <div className="relative max-w-[1280px] mx-auto grid md:grid-cols-12 gap-12 md:gap-20 items-center">
      <Reveal className="md:col-span-5">
        <FounderPortraitCard />
      </Reveal>

      <Reveal delay={120} className="md:col-span-7">
        <Eyebrow>The Architect · 03</Eyebrow>
        <h2 className="font-display mt-6 text-[clamp(36px,4.6vw,68px)] leading-[1.02] tracking-[-0.01em]">
          Dr. Ashley C. <em className="not-italic gold-sheen">Garcia Burns</em>
        </h2>
        <div className="mt-5 font-display italic text-[19px] md:text-[21px] leading-[1.5] text-[var(--gbg-bone)]">
          Transformation Executive. Researcher. Visionary. Builder.
        </div>
        <p className="mt-7 max-w-[58ch] text-[16px] md:text-[17px] leading-[1.85] text-[var(--gbg-bone)] font-light">
          Founder and CEO of Garcia Burns Group LLC, with almost two decades of
          experience leading enterprise transformation across global
          organizations. A Purdue-trained Doctor of Technology working at the
          intersection of strategy, leadership, AI, and human potential.
        </p>

        <div className="mt-10 grid grid-cols-3 gap-6 max-w-[480px]">
          {[
            { n: "15+", l: "Years of Practice" },
            { n: "4", l: "Ventures Held" },
            { n: "∞", l: "Possibilities" },
          ].map((s, i) => (
            <div
              key={s.l}
              className="border-t border-[rgba(201,163,82,0.3)] pt-4"
              data-testid={`founder-stat-${i}`}
            >
              <div className="font-display text-[36px] leading-none text-[var(--gbg-gold-bright)]">
                {s.n}
              </div>
              <div className="eyebrow mt-3 text-[var(--gbg-muted)]">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-6">
          <Link
            to="/founder"
            data-testid="founder-read-more"
            className="group inline-flex items-center gap-3 px-6 py-4 border border-[var(--gbg-gold)] text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-gold)] hover:bg-[var(--gbg-gold)] hover:text-[var(--gbg-ink)] transition-all duration-500"
          >
            <span>Read the Full Biography</span>
            <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
          </Link>
          <SmartNavLink
            href="/#contact"
            className="link-underline text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-cream)]/85"
          >
            Begin a conversation
          </SmartNavLink>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ----- /founder page: sub-components ----- */

const FOUNDER_PAGE_STATS = [
  { n: "15+", l: "Years of Practice" },
  { n: "4", l: "Ventures Held" },
  { n: "Purdue", l: "Doctor of Technology" },
  { n: "Global", l: "Enterprise Reach" },
];

const FounderPageHero = () => (
  <section className="relative pt-36 md:pt-44 pb-16 md:pb-20 px-6 md:px-12 overflow-hidden">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(50% 60% at 50% 0%, rgba(201,163,82,0.16) 0%, transparent 65%)",
      }}
    />
    <div className="relative max-w-[1280px] mx-auto">
      <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-muted)] mb-10">
        <Link
          to="/"
          data-testid="founder-back"
          className="link-underline text-[var(--gbg-cream)]/80 hover:text-[var(--gbg-gold)]"
        >
          Garcia Burns Group
        </Link>
        <span className="text-[var(--gbg-gold)]">/</span>
        <span className="text-[var(--gbg-gold)]">The Architect</span>
      </div>

      <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-end">
        <Reveal className="md:col-span-7">
          <Eyebrow>The Architect</Eyebrow>
          <h1 className="font-display mt-8 text-[clamp(48px,7vw,112px)] leading-[0.95] tracking-[-0.02em]">
            Dr. Ashley C.
            <br />
            <em className="not-italic gold-sheen">Garcia Burns</em>
          </h1>
          <div className="mt-8 font-display italic text-[21px] md:text-[24px] leading-[1.4] text-[var(--gbg-bone)]">
            Transformation Executive. Researcher. Visionary. Builder.
          </div>
          <div className="mt-6 font-italiana text-[12px] tracking-[0.4em] text-[var(--gbg-gold)]">
            FOUNDER · CEO
          </div>
        </Reveal>
        <Reveal delay={120} className="md:col-span-5">
          <FounderPortraitCard />
        </Reveal>
      </div>
    </div>
  </section>
);

const FounderPageStats = () => (
  <section className="border-y border-[rgba(201,163,82,0.18)] bg-[var(--gbg-ink-2)]">
    <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
      {FOUNDER_PAGE_STATS.map((s, i) => (
        <div key={s.l} data-testid={`founder-page-stat-${i}`}>
          <div className="font-display text-[34px] md:text-[42px] leading-none text-[var(--gbg-gold-bright)]">
            {s.n}
          </div>
          <div className="eyebrow mt-3 text-[var(--gbg-muted)]">{s.l}</div>
        </div>
      ))}
    </div>
  </section>
);

const FounderBiography = () => (
  <section className="relative py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[760px] mx-auto">
      <Reveal>
        <Eyebrow>Biography</Eyebrow>
      </Reveal>
      <Reveal delay={80} className="mt-10 space-y-6 text-[16px] md:text-[18px] leading-[1.85] text-[var(--gbg-bone)] font-light">
        <p>
          <span className="font-display text-[22px] text-[var(--gbg-cream)] float-left mr-3 mt-1 leading-none">
            D
          </span>
          r. Ashley C. Garcia Burns is the Founder and CEO of Garcia Burns Group
          LLC, a strategic holding company dedicated to building, investing in,
          and scaling businesses that drive transformation, accelerate
          leadership, and elevate human potential in the age of artificial
          intelligence.
        </p>
        <p>
          With almost two decades of experience leading enterprise transformation
          across global organizations, Ashley is recognized for her ability to
          align strategy, technology, and people to deliver meaningful,
          measurable, and lasting outcomes. Her work spans complex business
          transformations, organizational change, workforce modernization,
          leadership development, and technology adoption initiatives impacting
          thousands of employees across multiple industries.
        </p>
        <p>
          Her career spans organizational change, workforce transformation,
          healthcare, manufacturing, program and portfolio leadership,
          AI-enabled readiness, learning and capability development, and
          executive advisory. She has successfully led large-scale initiatives
          across global enterprises, helping organizations navigate ambiguity,
          build alignment, accelerate adoption, and translate strategy into
          execution.
        </p>
        <p>
          As a researcher and thought leader, Ashley holds a{" "}
          <span className="text-[var(--gbg-cream)]">
            Doctor of Technology degree from Purdue University in West Lafayette
          </span>
          . Her doctoral research focused on transformational leadership styles
          in manufacturing organizations and their impact on transformation
          outcomes. Her work bridges academic research and real-world
          application, advancing the understanding of how leadership influences
          employee engagement, organizational readiness, adoption, and
          long-term transformation success.
        </p>
        <p>
          Throughout her career, Ashley has been trusted to lead organizations
          through periods of significant change, bringing together business
          strategy, technology, leadership, and workforce transformation to
          create sustainable results. Her approach combines analytical rigor
          with a deep understanding of the human side of transformation,
          helping leaders build organizations that are resilient, adaptable,
          and prepared for the future.
        </p>
        <p>
          Today, through Garcia Burns Group and its portfolio of ventures,
          Ashley is focused on helping organizations navigate the intersection
          of leadership, transformation, artificial intelligence, and human
          potential—building businesses and solutions designed to thrive in an
          increasingly complex world.
        </p>
      </Reveal>
    </div>
  </section>
);

const FounderExpertise = () => (
  <section className="relative py-20 md:py-28 px-6 md:px-12 border-t border-[rgba(201,163,82,0.18)] bg-[var(--gbg-ink-2)]">
    <div className="max-w-[1280px] mx-auto">
      <Reveal>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <Eyebrow>Areas of Expertise</Eyebrow>
            <h3 className="font-display mt-5 text-[clamp(28px,3vw,42px)] leading-[1.1] tracking-[-0.01em]">
              A practice built at the
              <br />
              <em className="not-italic gold-sheen">intersection of disciplines.</em>
            </h3>
          </div>
          <div className="font-italiana text-[12px] tracking-[0.4em] text-[var(--gbg-muted)]">
            09 DOMAINS
          </div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-0">
          {EXPERTISE.map((item, i) => (
            <div
              key={item}
              data-testid={`expertise-${i}`}
              className="group flex items-baseline gap-4 py-5 border-t border-[rgba(201,163,82,0.22)] hover:border-[var(--gbg-gold)] transition-colors duration-500"
            >
              <span className="font-italiana text-[11px] tracking-[0.3em] text-[var(--gbg-gold)] tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display text-[19px] md:text-[21px] text-[var(--gbg-cream)] group-hover:text-[var(--gbg-gold-bright)] transition-colors duration-500">
                {item}
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const FounderQuote = () => (
  <section className="relative py-24 md:py-32 px-6 md:px-12">
    <Reveal>
      <figure className="relative max-w-[1000px] mx-auto text-center px-6">
        <div
          aria-hidden
          className="font-display absolute -top-12 left-1/2 -translate-x-1/2 text-[180px] leading-none text-[var(--gbg-gold)] opacity-[0.18] select-none pointer-events-none"
        >
          “
        </div>
        <blockquote className="relative font-display italic text-[clamp(28px,3.6vw,48px)] leading-[1.25] text-[var(--gbg-cream)] tracking-[-0.005em]">
          Our greatest advantage will always be how we{" "}
          <em className="not-italic gold-sheen">lead, adapt,</em> and bring
          people with us.
        </blockquote>
        <figcaption className="mt-10 flex flex-col items-center gap-3">
          <span className="block w-12 h-px bg-[var(--gbg-gold)]" />
          <span className="eyebrow text-[var(--gbg-gold)]">
            Dr. Ashley C. Garcia Burns
          </span>
          <span className="font-italiana text-[11px] tracking-[0.4em] text-[var(--gbg-muted)]">
            FOUNDER · CEO
          </span>
        </figcaption>
      </figure>
    </Reveal>
  </section>
);

const FounderCta = () => (
  <section className="relative py-20 md:py-28 px-6 md:px-12 border-t border-[rgba(201,163,82,0.18)] bg-gradient-to-b from-[var(--gbg-ink-2)] to-[var(--gbg-ink)]">
    <div className="max-w-[900px] mx-auto text-center">
      <Reveal>
        <Eyebrow>Continue</Eyebrow>
        <h3 className="font-display mt-6 text-[clamp(30px,4vw,52px)] leading-[1.05] tracking-[-0.01em]">
          Explore the <em className="not-italic gold-sheen">house of ventures</em>.
        </h3>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
          <SmartNavLink
            href="/#companies"
            data-testid="founder-cta-companies"
            className="group inline-flex items-center gap-3 px-7 py-4 border border-[var(--gbg-gold)] text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-gold)] hover:bg-[var(--gbg-gold)] hover:text-[var(--gbg-ink)] transition-all duration-500"
          >
            <span>View the Portfolio</span>
            <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
          </SmartNavLink>
          <SmartNavLink
            href="/#contact"
            data-testid="founder-cta-contact"
            className="link-underline text-[11px] tracking-[0.3em] uppercase text-[var(--gbg-cream)]/85"
          >
            Begin a conversation
          </SmartNavLink>
        </div>
      </Reveal>
    </div>
  </section>
);

/* Full /founder page */
const FounderPage = () => {
  useEffect(() => {
    document.title = "The Architect — Dr. Ashley C. Garcia Burns";
  }, []);

  return (
    <main data-testid="founder-page">
      <FounderPageHero />
      <FounderPageStats />
      <FounderBiography />
      <FounderExpertise />
      <FounderQuote />
      <FounderCta />
    </main>
  );
};

/* ---------- Contact ---------- */

const KEY_PLACEHOLDER = "YOUR_WEB3FORMS_ACCESS_KEY_HERE";
const isKeyConfigured = () => Boolean(WEB3FORMS_KEY) && WEB3FORMS_KEY !== KEY_PLACEHOLDER;

/* Form submission state + handler */
const useContactForm = () => {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setStatus("idle");
    setErrorMsg("");
  };

  const submitToWeb3Forms = async (formEl) => {
    const formData = new FormData(formEl);
    formData.append("access_key", WEB3FORMS_KEY || "");
    formData.append("subject", "New Inquiry — Garcia Burns Group Website");
    formData.append("from_name", "Garcia Burns Group Site");

    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    });
    const data = await res.json();
    if (!(res.ok && data.success)) {
      throw new Error(data.message || "Something went wrong. Please try again.");
    }
    formEl.reset();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!isKeyConfigured()) {
      setStatus("error");
      setErrorMsg(
        "Form is not yet configured. Please email info@garciaburnsgroup.com directly."
      );
      return;
    }

    setStatus("loading");
    try {
      await submitToWeb3Forms(e.target);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.message || "Network error. Please try again or email info@garciaburnsgroup.com."
      );
    }
  };

  return { status, errorMsg, onSubmit, reset };
};

const ContactSidebar = () => (
  <div className="md:col-span-5">
    <Reveal>
      <Eyebrow>Inquire · 04</Eyebrow>
    </Reveal>
    <Reveal delay={100}>
      <h2 className="font-display mt-8 text-[clamp(40px,5.4vw,84px)] leading-[0.98] tracking-[-0.02em]">
        Let&apos;s build
        <br />
        <em className="not-italic gold-sheen">what&apos;s next.</em>
      </h2>
    </Reveal>
    <Reveal delay={200}>
      <p className="mt-8 text-[16px] leading-[1.85] text-[var(--gbg-bone)] font-light max-w-[46ch]">
        For partnerships, advisory engagements, investment conversations, or
        opportunities to collaborate — Garcia Burns Group welcomes principled,
        ambitious correspondence.
      </p>
    </Reveal>
    <Reveal delay={300}>
      <div className="hairline mt-12" />
      <div className="mt-8 space-y-5">
        <div>
          <div className="eyebrow text-[var(--gbg-muted)] mb-2">Direct</div>
          <a
            href="mailto:info@garciaburnsgroup.com"
            data-testid="contact-direct-email"
            className="link-underline font-display text-[20px] md:text-[22px] text-[var(--gbg-cream)]"
          >
            info@garciaburnsgroup.com
          </a>
        </div>
        <div>
          <div className="eyebrow text-[var(--gbg-muted)] mb-2">Availability</div>
          <div className="font-display text-[16px] text-[var(--gbg-cream)]/85">
            By appointment · Worldwide
          </div>
        </div>
      </div>
    </Reveal>
  </div>
);

const ContactSuccess = ({ onReset }) => (
  <div
    data-testid="contact-success"
    className="flex flex-col items-center justify-center text-center py-12 px-4"
  >
    <div className="font-display text-[64px] leading-none gold-sheen mb-6">✦</div>
    <div className="font-display text-[28px] md:text-[32px] text-[var(--gbg-cream)] leading-tight">
      Thank you.
    </div>
    <p className="mt-4 max-w-[42ch] text-[15px] leading-[1.7] text-[var(--gbg-bone)] font-light">
      Your message has been received. We&apos;ll respond within one business day.
    </p>
    <button
      type="button"
      onClick={onReset}
      data-testid="contact-send-another"
      className="mt-10 text-[11px] tracking-[0.3em] uppercase px-6 py-3 border border-[var(--gbg-gold)]/60 text-[var(--gbg-gold)] hover:bg-[var(--gbg-gold)] hover:text-[var(--gbg-ink)] transition-all duration-500"
    >
      Send Another
    </button>
  </div>
);

const ContactSubmitButton = ({ loading }) => (
  <button
    type="submit"
    data-testid="contact-submit"
    disabled={loading}
    className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-[var(--gbg-gold)] text-[var(--gbg-ink)] text-[11px] tracking-[0.32em] uppercase hover:bg-[var(--gbg-gold-bright)] transition-all duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
  >
    <span>{loading ? "Sending…" : "Send Inquiry"}</span>
    <span className="transition-transform duration-500 group-hover:translate-x-1">↗</span>
  </button>
);

const ContactFormBody = ({ status, errorMsg, onSubmit }) => {
  const loading = status === "loading";
  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-7">
      {/* honeypot */}
      <input
        type="checkbox"
        name="botcheck"
        tabIndex={-1}
        autoComplete="off"
        style={{ display: "none" }}
      />

      <div className="grid sm:grid-cols-2 gap-7">
        <Field label="Name" name="name" required testid="contact-name" disabled={loading} />
        <Field label="Company" name="company" testid="contact-company" disabled={loading} />
      </div>

      <Field
        label="Email"
        name="email"
        type="email"
        required
        testid="contact-email"
        disabled={loading}
      />

      <Field
        label="Nature of Inquiry"
        name="inquiry_type"
        placeholder="Advisory · Partnership · Investment · Press · Other"
        testid="contact-inquiry"
        disabled={loading}
      />

      <Field
        label="Message"
        name="message"
        as="textarea"
        rows={5}
        required
        testid="contact-message"
        disabled={loading}
      />

      {status === "error" && (
        <div
          data-testid="contact-error"
          className="text-[12px] tracking-[0.18em] uppercase border border-red-500/40 text-red-300/90 bg-red-950/20 px-4 py-3"
        >
          {errorMsg}
        </div>
      )}

      <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <p className="text-[11px] leading-[1.6] text-[var(--gbg-muted)] max-w-[44ch] tracking-[0.04em]">
          By submitting, you agree to be contacted by Garcia Burns Group regarding your inquiry.
        </p>
        <ContactSubmitButton loading={loading} />
      </div>
    </form>
  );
};

const Contact = () => {
  const { status, errorMsg, onSubmit, reset } = useContactForm();

  return (
    <section
      id="contact"
      data-testid="contact"
      className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden border-t border-[rgba(201,163,82,0.18)] bg-gradient-to-b from-[var(--gbg-ink-2)] to-[var(--gbg-ink)]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 40% at 50% 100%, rgba(201,163,82,0.16) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
        <ContactSidebar />

        <div className="md:col-span-7">
          <Reveal delay={150}>
            <div className="relative border border-[rgba(201,163,82,0.28)] bg-gradient-to-b from-[rgba(255,255,255,0.025)] to-[rgba(255,255,255,0.005)] p-8 md:p-12">
              <span className="absolute -top-px left-8 px-3 bg-[var(--gbg-ink)] text-[10px] tracking-[0.4em] uppercase text-[var(--gbg-gold)]">
                Correspondence
              </span>

              {status === "success" ? (
                <ContactSuccess onReset={reset} />
              ) : (
                <ContactFormBody status={status} errorMsg={errorMsg} onSubmit={onSubmit} />
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* Field — luxury underlined input/textarea with floating label */
const FIELD_CLASS =
  "w-full bg-transparent border-0 border-b border-[rgba(201,163,82,0.3)] focus:border-[var(--gbg-gold)] outline-none text-[15px] md:text-[16px] text-[var(--gbg-cream)] py-3 placeholder:text-[var(--gbg-muted)]/60 transition-colors font-light";

const Field = ({
  label,
  name,
  type = "text",
  as = "input",
  required = false,
  placeholder = "",
  rows = 4,
  testid,
  disabled = false,
}) => {
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const isFloating = focused || hasValue;

  const handleFocus = () => setFocused(true);
  const handleBlur = (e) => {
    setFocused(false);
    setHasValue(Boolean(e.target.value));
  };
  const handleChange = (e) => setHasValue(Boolean(e.target.value));

  return (
    <label className="block relative pt-5">
      <span
        className={`absolute left-0 pointer-events-none transition-all duration-300 tracking-[0.24em] uppercase ${
          isFloating
            ? "top-0 text-[10px] text-[var(--gbg-gold)]"
            : "top-[26px] text-[12px] text-[var(--gbg-muted)]"
        }`}
      >
        {label}
        {required && <span className="ml-1 text-[var(--gbg-gold)]">*</span>}
      </span>
      {as === "textarea" ? (
        <textarea
          name={name}
          required={required}
          placeholder={focused ? placeholder : ""}
          disabled={disabled}
          rows={rows}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          data-testid={testid}
          className={FIELD_CLASS}
        />
      ) : (
        <input
          name={name}
          type={type}
          required={required}
          placeholder={focused ? placeholder : ""}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          data-testid={testid}
          className={FIELD_CLASS}
        />
      )}
    </label>
  );
};

/* ---------- Footer ---------- */

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative px-6 md:px-12 py-14 border-t border-[rgba(201,163,82,0.22)] bg-[var(--gbg-ink)]"
  >
    <div className="max-w-[1280px] mx-auto grid md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="" className="w-10 h-10 object-contain" />
          <div className="leading-tight">
            <div className="font-italiana text-[16px] tracking-[0.18em]">
              GARCIA BURNS
            </div>
            <div className="text-[10px] tracking-[0.4em] text-[var(--gbg-gold)] mt-1">
              GROUP · LLC
            </div>
          </div>
        </div>
        <p className="mt-6 max-w-[42ch] text-[13px] leading-[1.8] text-[var(--gbg-muted)] font-light">
          A strategic holding company building, owning, and scaling ventures in
          transformation, intelligence, leadership, and human potential.
        </p>
      </div>

      <div className="md:col-span-3">
        <div className="eyebrow text-[var(--gbg-gold)] mb-5">Navigate</div>
        <ul className="space-y-3">
          {NAV.map((n) => (
            <li key={n.label}>
              <SmartNavLink
                href={n.href}
                to={n.to}
                className="text-[13px] text-[var(--gbg-cream)]/75 hover:text-[var(--gbg-gold)] transition-colors"
              >
                {n.label}
              </SmartNavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <div className="eyebrow text-[var(--gbg-gold)] mb-5">Correspondence</div>
        <a
          href="mailto:info@garciaburnsgroup.com"
          className="link-underline text-[14px] text-[var(--gbg-cream)]"
          data-testid="footer-email"
        >
          info@garciaburnsgroup.com
        </a>
        <div className="mt-6 text-[12px] tracking-[0.2em] text-[var(--gbg-muted)] uppercase">
          By appointment · Worldwide
        </div>
      </div>
    </div>

    <div className="max-w-[1280px] mx-auto mt-14 pt-6 border-t border-[rgba(201,163,82,0.15)] flex flex-col md:flex-row justify-between gap-4 text-[11px] tracking-[0.24em] uppercase text-[var(--gbg-muted)]">
      <div>© {new Date().getFullYear()} Garcia Burns Group LLC · All Rights Reserved</div>
      <div className="flex items-center gap-3">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--gbg-gold)]" />
        Building what&apos;s next
      </div>
    </div>
  </footer>
);

/* ---------- App ---------- */

const HomePage = () => {
  useEffect(() => {
    document.title = "Garcia Burns Group LLC — Building What's Next";
  }, []);
  return (
    <main>
      <Hero />
      <Marquee />
      <Ethos />
      <Companies />
      <Contact />
    </main>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      <div className="App grain" data-testid="app-root">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/founder" element={<FounderPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
